/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { FaPlus, FaInfoCircle, FaExclamationTriangle } from "react-icons/fa";
import { TimeSlotRow } from './TimeSlotRow';
import { SlotsData  } from 'interfaces/ISlots';
import { cn } from 'utils/utils';
import usePreRender from 'hooks/usePreRender';

interface DayScheduleProps {
  capacityId: string;  
  scheduleId: string;
  activeDay: string;
  dayId: string;
  form: UseFormReturn<SlotsData>;
  deliveryMethod?: string;
}

export const DaySchedule: React.FC<DayScheduleProps> = ({ 
  capacityId,
  scheduleId,
  activeDay,
  dayId, 
  form
}) => {
  const { _color } = usePreRender()
  const [capacityValidation, setCapacityValidation] = useState({ isExceeded: false, totalSlot: 0, totalAvailable: 0 });
  
  const { fields, append, remove } = useFieldArray<any>({
    control: form.control,
    name: `${dayId}`,
  });

  const addTimeSlot = () => {
    append({ 
      from: "09:00",
      to: "17:00",
      capacity: 0,
      status: true,
      label: "Horario", 
    });
  };

  // Get filtered capacities based on deliveryType and activeDay
  const getFilteredCapacities = () => {
    try {
      // Get all form values to avoid type issues
      const allValues = form.getValues();
      
      // Navigate to the capacity array using the capacityId path
      const capacityPath = capacityId.split('.');
      let capacities:any = allValues;
      for (const key of capacityPath) {
        capacities = capacities?.[key];
      }
      
      // Navigate to the schedule data using the scheduleId path
      const schedulePath = scheduleId.split('.');
      let scheduleData:any = allValues;
      for (const key of schedulePath) {
        scheduleData = scheduleData?.[key];
      }

      const deliveryType = scheduleData?.deliveryType;
      const activeDayNumber = parseInt(activeDay);

      if (!Array.isArray(capacities) || !deliveryType || !activeDayNumber) {
        return [];
      }

      return capacities.filter((capacity: any) => 
        capacity?.deliveryType === deliveryType && 
        Array.isArray(capacity?.daysOfWeek) &&
        capacity.daysOfWeek.includes(activeDayNumber)
      );
    } catch (error) {
      console.error('Error filtering capacities:', error);
      return [];
    }
  };

  // Calculate total capacity from time slots
  const getTotalSlotCapacity = () => {
    try {
      const allValues = form.getValues();
      const dayPath = dayId.split('.');
      let daySlots:any = allValues;
      for (const key of dayPath) {
        daySlots = daySlots?.[key];
      }
      
      if (!Array.isArray(daySlots)) return 0;
      
      return daySlots.reduce((total: number, slot: any) => {
        const capacity = parseInt(slot?.capacity) || 0;
        return total + capacity;
      }, 0);
    } catch (error) {
      console.error('Error calculating slot capacity:', error);
      return 0;
    }
  };

  // Validate capacity whenever form values change
  const validateCapacity = () => {
    const filteredCapacities = getFilteredCapacities();
    const totalSlotCapacity = getTotalSlotCapacity();
    const totalAvailableCapacity = filteredCapacities.reduce((total: number, cap: any) => total + (cap.capacity || 0), 0);
    const isCapacityExceeded = totalSlotCapacity > totalAvailableCapacity;

    setCapacityValidation({
      isExceeded: isCapacityExceeded,
      totalSlot: totalSlotCapacity,
      totalAvailable: totalAvailableCapacity
    });
  };

  // Watch for form changes
  useEffect(() => {
    const subscription = form.watch(() => {
      validateCapacity();
    });
    
    // Initial validation
    validateCapacity();
    
    return () => subscription.unsubscribe();
  }, [form, dayId, capacityId, scheduleId, activeDay]);

  const filteredCapacities = getFilteredCapacities();

  return (
    <div>
      <h2 className="px-4 py-3 font-medium text-gray-700 bg-gray-50 border-b border-gray-200 flex flex-row inline-flex items-center gap-2">
        Tabla de configuración
        <button
          type="button"
          onClick={addTimeSlot}
          className= {cn(
            "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors max-h-[25px]",
            `text_${_color}`,
          )}
        >
          <FaPlus size={16} />
          Añadir
        </button>

        {/* Capacity List */}
        {filteredCapacities.length > 0 && (
          <>
            <div className="h-6 w-px bg-gray-300"></div>
            <div className="flex items-center gap-2">
              Capacidad de Despacho: 
              <span className="text-sm text-gray-600">
                {filteredCapacities.map((cap: any) => cap.capacity).join(', ')}
              </span>
              <div className="relative group">
                <FaInfoCircle 
                  size={14} 
                  className="text-gray-400 hover:text-gray-600 cursor-help" 
                />
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  Estas son las capacidades de despacho configuradas para este dia
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                </div>
              </div>
            </div>
          </>
        )}

      </h2>

      {/* Capacity Exceeded Error */}
      {capacityValidation.isExceeded && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2 text-red-700">
          <FaExclamationTriangle size={16} className="text-red-500" />
          <span className="text-sm font-medium">
            Error: La capacidad total de los horarios ({capacityValidation.totalSlot}) excede la capacidad de despacho disponible ({capacityValidation.totalAvailable})
          </span>
        </div>
      )}

      <div>
        <div className="bg-gray-50 p-4 border-b border-gray-200">
          <div className="grid grid-cols-[150px_150px_150px_150px_100px] gap-3">
            <div className="text-xs font-medium text-gray-500 flex flex-col justify-center items-center">TIEMPO DE INICIO</div>
            <div className="text-xs font-medium text-gray-500 flex flex-col justify-center items-center">TIEMPO DE FIN</div>
            <div className="text-xs font-medium text-gray-500 flex flex-col justify-center items-center">CAPACIDAD</div>
            <div className="text-xs font-medium text-gray-500 flex flex-col justify-center items-center">NOMBRE</div>
            <div className="text-xs font-medium text-gray-500 flex flex-col justify-center items-center">ESTADO</div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {fields.map((field, index) => (
            <TimeSlotRow
              key={field.id}
              index={index}
              register={form.register}
              onDelete={() => remove(index)}
              slotId={`${dayId}.${index}`}
            />
          ))}

          {fields.length === 0 && (
            <div className="text-center py-6 px-3 text-gray-500 text-sm">
              Ningun horario ha sido añadido
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};
